import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
// Mesmo layout do encaminhamento para a esteira juridica: resumo do contrato,
// aviso do efeito e um campo de texto.
import {
  AvisoBox,
  AvisoTexto,
  AvisoTitulo,
  CompanyButtonContainer,
  ForwardContainer,
  ResumoContainer,
  ResumoItem,
  ResumoLabel,
  ResumoValue,
} from 'components/Modal/BillingForward/styled';
import Wrapper from 'components/Modal/Wrapper';
import { CoreContext } from 'context/CoreContext';
import { useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CancelRenegotiation } from 'services/cobranca';
import { exposeStrapiError } from 'utils';
import { CANCELLED_STATUS } from 'utils/billing';

// Quem ve o botao. Comparado com `true` estrito: o administrador nao dispensa a
// flag, o backend devolve 403 para admin sem ela.
export const canCancelRenegotiation = (user) =>
  user?.cancela_renegociacao === true;

// Por que a renegociacao nao pode ser cancelada, ou `null` se pode. Espelha as
// regras do backend para o botao ja nascer desabilitado com o motivo, em vez
// de o operador descobrir pelo 400. O backend confere tudo de novo.
// `row` e a linha do Billing da cobranca NOVA (ver contractFields).
export const cancelBlockReason = (row) => {
  if (!row?.renegotiationOrigin) {
    return 'Esta cobrança não foi gerada por renegociação.';
  }
  if (row?.status === CANCELLED_STATUS) {
    return 'Esta renegociação já foi cancelada.';
  }
  if (
    (row?.renegotiations || []).some(
      (item) => item?.status !== CANCELLED_STATUS
    )
  ) {
    return 'Esta cobrança foi renegociada de novo: cancele primeiro a renegociação mais recente.';
  }
  if ((row?.titulos || []).some((titulo) => titulo?.status === 'baixado')) {
    return 'A renegociação já tem baixa e não pode ser cancelada.';
  }
  return null;
};

// O 400 de "ja foi cancelada" e clique duplo ou dois operadores no mesmo
// contrato: o resultado desejado ja vale, entao avisa e recarrega.
const alreadyCancelled = (result) =>
  `${result?.error?.message || ''}`.toLowerCase().includes('já foi cancelada');

const plural = (n, singular, pluralForm) => (n === 1 ? singular : pluralForm);

// Cancelamento manual de uma renegociacao, aberto pela aba Renegociacao do
// Historico na cobranca nova. `dados` e a linha do Billing dessa cobranca. A
// justificativa e obrigatoria e vai para o log `cancelamento_renegociacao`.
export default function BillingCancelRenegotiation({ dados, reload }) {
  const { setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const cobrancaId = dados?.id;
  const original =
    dados?.renegotiationOrigin?.contrato ||
    (dados?.renegotiationOrigin?.id ? `#${dados.renegotiationOrigin.id}` : '-');

  const close = () => setModal(null);

  // Desistir volta para o Historico, de onde o cancelamento foi aberto.
  const back = () => setModal({ type: 'historico_cobranca', dados, reload });

  const formItems = [
    {
      ref: 'justificativa',
      placeholder: 'Justificativa do cancelamento',
      type: 'text',
      full: true,
      multiline: true,
      maxRows: 4,
      height: 100,
      required: true,
    },
  ];

  const confirm = async () => {
    if (loading) return;
    if (!cobrancaId) {
      toast.error('Não foi possível cancelar: cobrança não identificada.');
      return;
    }
    // getForm() valida a justificativa obrigatoria e emite o toast sozinho.
    const form = formRef.current?.getForm?.();
    if (!form) return;
    const justificativa = `${form?.justificativa || ''}`.trim();
    if (!justificativa) {
      toast.error('Informe a justificativa do cancelamento.');
      return;
    }

    setLoading(true);
    try {
      const result = await CancelRenegotiation(cobrancaId, {
        data: { justificativa },
      });

      if (result?.error) {
        if (result?.error?.status === 403) {
          toast.error('Você não tem permissão para cancelar renegociações.');
          return;
        }
        if (alreadyCancelled(result)) {
          toast.warning('Esta renegociação já foi cancelada.');
          close();
          reload?.();
          return;
        }
        exposeStrapiError(result);
        return;
      }
      if (exposeStrapiError(result)) return;

      const restored = Array.isArray(result?.titulos_restaurados)
        ? result.titulos_restaurados.length
        : 0;
      toast.success(
        `Renegociação cancelada. ${
          restored
            ? `${restored} ${plural(
                restored,
                'título voltou',
                'títulos voltaram'
              )} a ser cobrado${restored > 1 ? 's' : ''} no contrato ${
                result?.contrato_original || original
              }.`
            : `O contrato ${original} volta a ser cobrado.`
        }`
      );
      close();
      reload?.();
    } catch (err) {
      console.error('Erro ao cancelar a renegociação:', err);
      toast.error('Erro ao cancelar a renegociação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper title="Cancelar renegociação" onClose={close}>
      <ForwardContainer>
        <ResumoContainer>
          <ResumoItem>
            <ResumoLabel>Renegociação</ResumoLabel>
            <ResumoValue>{dados?.contrato || cobrancaId || '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Contrato original</ResumoLabel>
            <ResumoValue>{original}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Cliente</ResumoLabel>
            <ResumoValue>{dados?.customer || '-'}</ResumoValue>
          </ResumoItem>
        </ResumoContainer>

        <AvisoBox $color="#dd4952">
          <AvisoTitulo>Confirme antes de seguir</AvisoTitulo>
          <AvisoTexto>
            A renegociação {dados?.contrato || ''} é cancelada e todos os
            títulos dela, inclusive a entrada, saem da carteira. As parcelas
            renegociadas voltam para o contrato {original}, que volta a ser
            cobrado e retorna à régua. Os avisos que já passaram não são
            reenviados. O cancelamento não pode ser desfeito.
          </AvisoTexto>
        </AvisoBox>

        <FormCore ref={formRef} formItems={formItems} />

        <CompanyButtonContainer>
          <Button color="lightgrey" onClick={back} disabled={loading} nospace>
            Voltar
          </Button>
          <Button color="error" onClick={confirm} disabled={loading} nospace>
            {loading ? 'Cancelando...' : 'Cancelar renegociação'}
          </Button>
        </CompanyButtonContainer>
      </ForwardContainer>
    </Wrapper>
  );
}
